import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  ExpenseStatusEnum,
  ExpensesWhereColumn,
  GetExpensesDocument,
  type GetExpensesQuery,
  type GetExpensesQueryVariables,
  SqlOperator,
} from "@src/__generated__/graphql";
import { ExpenseTypeEnum } from "@src/__generated__/urql-graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { appStore } from "@src/stores/AppStore";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { camelCase } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import type { TRelatedExpense } from "../../../models/types";
import type { Store } from "../../../store";

// Types from queries - these are what we fetch from API
type ExpenseItem = NonNullable<GetExpensesQuery["expenses"]>["data"][number];

export type DisclosureStateData = {
  selectedIds: string[];
  contractorId: string;
  expenseType: ExpenseTypeEnum | undefined;
  onConfirm: (expenses: TRelatedExpense[]) => void;
};

export class RelatedDocumentsEditModalStore {
  readonly key = "related-documents-edit-expenses";

  @observable.ref
  selectedExpenseIds: string[] = [];

  @observable
  pagination = new PaginationState(this.key, {
    perPage: 50,
    perPageOptions: [50, 100, 150],
    onChangePagination: () => {
      if (!this.disclosureState.isOpen) return;
      this.fetchExpenses();
    },
  });

  /** Currently displayed data (from API) */
  @observable.ref
  displayedExpenses: ExpenseItem[] = [];

  /** Map to store ID -> expense data for building selected items across pages */
  @observable
  expenseItemsMap = new Map<string, TRelatedExpense>();

  expenseFilters = new Filters([
    new Filter({
      column: ExpensesWhereColumn.ContractorId,
      operator: SqlOperator.Eq,
      options: [],
      value: [],
    }),
    new Filter({
      column: ExpensesWhereColumn.Status,
      operator: SqlOperator.Eq,
      options: [],
      value: ExpenseStatusEnum.Approved,
    }),
    new Filter({
      column: ExpensesWhereColumn.Type,
      operator: SqlOperator.In,
      options: [],
      value: [],
    }),
  ]);

  /** Fetcher for expenses */
  private expenseFetcher = new FetchHelper<
    GetExpensesQuery,
    GetExpensesQueryVariables
  >(GetExpensesDocument);

  readonly disclosureState = new DisclosureState<DisclosureStateData>({
    onOpen: (data: DisclosureStateData | undefined) => {
      if (!data) return;
      if (!data.expenseType) {
        captureEvent({
          message:
            "FE: No expense type passed while trying to open related documents edit modal.",
        });
        appStore.UIStore.toast({
          status: "error",
          title: t`Something went wrong, please try again later.`,
        });
        return;
      }

      this.selectedExpenseIds = data.selectedIds;
      this.displayedExpenses = [];

      // Set contractor filter
      this.expenseFilters.filtersByColumn
        .get(ExpensesWhereColumn.ContractorId)
        ?.setValue(data.contractorId);

      // Set type filter based on expense type
      this.expenseFilters.filtersByColumn
        .get(ExpensesWhereColumn.Type)
        ?.setValue(
          camelCase(this.getFilterTypeForExpenseType(data.expenseType)),
        );

      this.fetchExpenses();
    },
    onClose: () => {
      // Reset state on close
      this.selectedExpenseIds = [];
      this.pagination.resetPage();
      this.displayedExpenses = [];
      this.expenseItemsMap.clear();
    },
  });

  constructor(public parentStore: Store) {
    makeObservable(this);
  }

  @computed
  get isLoading(): boolean {
    return this.expenseFetcher.isLoading.value;
  }

  @action.bound
  getFilterTypeForExpenseType(
    expenseType: ExpenseTypeEnum,
  ): ExpenseTypeEnum | undefined {
    switch (expenseType) {
      case ExpenseTypeEnum.Proforma:
      case ExpenseTypeEnum.Invoice:
        return ExpenseTypeEnum.PaymentReceipt;
      case ExpenseTypeEnum.PaymentReceipt:
        return ExpenseTypeEnum.Proforma;
      default:
        return undefined;
    }
  }

  @action.bound
  async fetchExpenses(): Promise<void> {
    const [data, error] = await this.expenseFetcher.fetch({
      page: this.pagination.currentPage,
      first: this.pagination.perPage,
      filters: {
        where: this.expenseFilters.asWhereParam,
      },
    });

    if (error || !data.expenses) return;

    this.pagination.setFromPaginatorInfo(data.expenses.paginatorInfo);
    this.displayedExpenses = data.expenses.data;

    // Store expense data for building selected items across pages
    data.expenses.data.forEach((expense: ExpenseItem) => {
      this.expenseItemsMap.set(expense.id, {
        id: expense.id,
        expense_no: expense.expense_no,
        contractor: expense.contractor
          ? {
              id: expense.contractor.id,
              legal_name: expense.contractor.legal_name,
            }
          : null,
        stats: expense.stats
          ? {
              total_amount_with_vat_in_currency:
                expense.stats.total_amount_with_vat_in_currency,
            }
          : null,
        type: expense.type,
        status: expense.status,
        date_of_supply: expense.date_of_supply,
      });
    });
  }

  @action.bound
  setSelectedExpenseIds(ids: string[]): void {
    this.selectedExpenseIds = ids;
  }

  @action.bound
  handleConfirm(): void {
    if (!this.disclosureState.additionalData) {
      appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong, please try again later.`,
      });
      return;
    }

    const selectedExpenses = this.selectedExpenseIds
      .map((id) => this.expenseItemsMap.get(id))
      .filter((expense): expense is TRelatedExpense => expense != null);

    this.disclosureState.additionalData.onConfirm(selectedExpenses);
    this.disclosureState.onClose();
  }
}
